import { Temporal } from "@js-temporal/polyfill";
import { occupancyLevel } from "./capacity.js";
import { addDaysToIsoDate, gymDayContext, occurrencesBetween, } from "./schedule.js";
export function projectAttendance(gym, schedules, fromInstant, days) {
    const from = Temporal.Instant.from(fromInstant);
    const startDate = from.toZonedDateTimeISO(gym.timeZone).toPlainDate().toString();
    // Millisecondi e `Date.parse` invece di `Temporal.Instant` per elemento: gli
    // istanti delle occorrenze hanno tutti lo stesso formato (PERF-09).
    const fromMs = from.epochMilliseconds;
    return projectCoachCalendar(gym, schedules, startDate, Math.max(0, days) + 1)
        .flatMap((day) => day.slots)
        .filter((entry) => Date.parse(entry.occurrence.startsAt) >= fromMs);
}
/** Un solo collatore per tutti gli ordinamenti: `localeCompare(…, "it")` ne costruiva uno per confronto. */
const byDisplayName = new Intl.Collator("it");
export function projectCoachCalendar(gym, schedules, startDate, dayCount) {
    if (!Number.isInteger(dayCount) || dayCount <= 0)
        return [];
    const firstDate = Temporal.PlainDate.from(startDate);
    const firstIsoDate = firstDate.toString();
    const days = Array.from({ length: dayCount }, (_, offset) => {
        const date = offset === 0 ? firstIsoDate : addDaysToIsoDate(firstIsoDate, offset);
        // Il contesto della giornata — chiusura, slot e occorrenze gia' datate — e'
        // memoizzato per gym: qui e in `occurrencesBetween` si legge la stessa voce,
        // invece di rifare `exceptions.find` e l'aritmetica degli istanti per
        // giorno e per atleta (PERF-05).
        const context = gymDayContext(gym, date);
        return {
            date,
            closed: context.closed,
            slots: context.slots.map((slot) => {
                const occurrence = context.occurrenceBySlotId.get(slot.id);
                if (!occurrence)
                    throw new Error(`Slot non trovato per la data ${date}: ${slot.id}`);
                return {
                    key: occurrence.key,
                    occurrence,
                    capacity: slot.capacity,
                    state: occupancyLevel(0, slot.capacity),
                    participants: [],
                };
            }),
        };
    });
    const result = new Map(days.flatMap((day) => day.slots.map((slot) => [slot.key, slot])));
    const fromInstant = firstDate
        .toZonedDateTime({ timeZone: gym.timeZone, plainTime: Temporal.PlainTime.from("00:00") })
        .toInstant()
        .toString();
    for (const schedule of schedules) {
        for (const occurrence of occurrencesBetween(gym, schedule, fromInstant, dayCount - 1)) {
            if (occurrence.status === "conflicted")
                continue;
            const current = result.get(occurrence.key);
            if (!current)
                continue;
            if (!current.participants.some((person) => person.memberId === schedule.memberId)) {
                current.participants.push({
                    memberId: schedule.memberId,
                    displayName: schedule.displayName,
                });
            }
        }
    }
    // Ordinamento e livello una volta sola, a valle: dentro il ciclo costavano un
    // sort completo per ogni presenza inserita.
    for (const slot of result.values()) {
        if (slot.participants.length === 0)
            continue;
        slot.participants.sort((a, b) => byDisplayName.compare(a.displayName, b.displayName));
        slot.state = occupancyLevel(slot.participants.length, slot.capacity);
    }
    return days;
}
export function deriveAlerts(before, after, now = new Date().toISOString()) {
    const previous = new Map(before.map((entry) => [entry.key, entry]));
    return after.flatMap((entry) => {
        const old = previous.get(entry.key);
        if (entry.state === "over" && old?.state !== "over") {
            return [
                {
                    id: `capacity-over:${entry.key}`,
                    slotKey: entry.key,
                    kind: "capacity-over",
                    createdAt: now,
                },
            ];
        }
        if (entry.state === "over" && old && old.participants.length !== entry.participants.length) {
            return [
                {
                    id: `critical-change:${entry.key}:${entry.participants.length}`,
                    slotKey: entry.key,
                    kind: "critical-change",
                    createdAt: now,
                },
            ];
        }
        return [];
    });
}
